import { useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowUp, ArrowDown, ArrowUpDown, Filter, Search } from "lucide-react";

const NUM_OPS = [
  { v: "gt", label: "Maggiore di (>)" },
  { v: "gte", label: "Maggiore o uguale (≥)" },
  { v: "lt", label: "Minore di (<)" },
  { v: "lte", label: "Minore o uguale (≤)" },
  { v: "eq", label: "Uguale a (=)" },
  { v: "between", label: "Compreso tra" },
];

function TextFilter({ col, values, selected, setSelected }) {
  const [q, setQ] = useState("");
  const visible = values.filter((v) => v.toLowerCase().includes(q.toLowerCase()));
  const sel = selected || new Set(values);
  const allChecked = sel.size === values.length;
  const toggle = (v) => {
    const next = new Set(sel);
    next.has(v) ? next.delete(v) : next.add(v);
    setSelected(col.key, next.size === values.length ? null : next);
  };
  const toggleAll = () => setSelected(col.key, allChecked ? new Set() : null);
  return (
    <div className="w-60">
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca valore..." data-testid={`filter-search-${col.key}`} className="h-8 pl-8 text-sm" />
      </div>
      <div className="mb-1 flex items-center justify-between gap-2 border-b border-slate-100 pb-1.5">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Checkbox checked={allChecked} onCheckedChange={toggleAll} /> Seleziona tutto
        </label>
        <button type="button" onClick={() => setSelected(col.key, new Set())} data-testid={`filter-deselect-all-${col.key}`} className="text-xs font-medium text-[#002FA7] hover:underline">Deseleziona tutto</button>
      </div>
      <div className="max-h-44 overflow-auto py-1">
        {visible.length === 0 ? <p className="px-1 py-2 text-xs text-slate-400">Nessun valore</p> : visible.map((v) => (
          <label key={v} className="flex items-center gap-2 py-0.5 text-sm text-slate-700">
            <Checkbox checked={sel.has(v)} onCheckedChange={() => toggle(v)} /> <span className="truncate">{v || "—"}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function NumberFilter({ col, filter, setFilter }) {
  const f = filter || { op: "gte", v1: "", v2: "" };
  const upd = (patch) => setFilter(col.key, { ...f, ...patch });
  return (
    <div className="w-56 space-y-2">
      <p className="text-xs font-medium text-slate-500">Filtra per condizione</p>
      <select value={f.op} onChange={(e) => upd({ op: e.target.value })} data-testid={`filter-op-${col.key}`} className="w-full rounded-md border border-slate-200 p-2 text-sm focus:border-[#002FA7] focus:outline-none">
        {NUM_OPS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
      <div className="flex items-center gap-2">
        <Input type="number" step="0.01" value={f.v1} onChange={(e) => upd({ v1: e.target.value })} placeholder="Valore" data-testid={`filter-v1-${col.key}`} className="h-8 text-sm" />
        {f.op === "between" && <Input type="number" step="0.01" value={f.v2} onChange={(e) => upd({ v2: e.target.value })} placeholder="a" data-testid={`filter-v2-${col.key}`} className="h-8 text-sm" />}
      </div>
      <Button variant="ghost" size="sm" onClick={() => setFilter(col.key, null)} className="h-7 w-full text-xs">Azzera</Button>
    </div>
  );
}

export function ExcelTable({ data = [], columns, testid, initialSort, emptyIcon: EmptyIcon, emptyText = "Nessun dato", footer, onRowClick, rowTestId, searchKeys, searchPlaceholder = "Cerca..." }) {
  const [sort, setSort] = useState(initialSort || null);
  const [textFilters, setTextFilters] = useState({});
  const [numFilters, setNumFilters] = useState({});
  const [query, setQuery] = useState("");

  const distinct = useMemo(() => {
    const m = {};
    columns.filter((c) => c.type !== "number").forEach((c) => {
      m[c.key] = Array.from(new Set(data.map((r) => String(r[c.key] ?? "")))).sort((a, b) => a.localeCompare(b, "it", { numeric: true, sensitivity: "base" }));
    });
    return m;
  }, [data, columns]);

  const setTextSel = (key, set) => setTextFilters((p) => { const n = { ...p }; if (set == null) delete n[key]; else n[key] = set; return n; });
  const setNumFil = (key, f) => setNumFilters((p) => { const n = { ...p }; if (f == null) delete n[key]; else n[key] = f; return n; });

  const filtered = useMemo(() => {
    let rows = data.filter((r) => {
      if (searchKeys && query.trim()) {
        const q = query.toLowerCase();
        if (!searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(q))) return false;
      }
      for (const [k, sel] of Object.entries(textFilters)) {
        if (sel && !sel.has(String(r[k] ?? ""))) return false;
      }
      for (const [k, f] of Object.entries(numFilters)) {
        if (!f || f.v1 === "" || f.v1 == null) continue;
        const val = Number(r[k]) || 0; const a = parseFloat(f.v1);
        if (isNaN(a)) continue;
        if (f.op === "gt" && !(val > a)) return false;
        if (f.op === "gte" && !(val >= a)) return false;
        if (f.op === "lt" && !(val < a)) return false;
        if (f.op === "lte" && !(val <= a)) return false;
        if (f.op === "eq" && !(val === a)) return false;
        if (f.op === "between") { const b = parseFloat(f.v2); if (!(val >= a && val <= (isNaN(b) ? a : b))) return false; }
      }
      return true;
    });
    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      const gv = col?.sortValue || ((r) => r[sort.key]);
      rows = [...rows].sort((x, y) => { const a = gv(x), b = gv(y); let cmp; if (typeof a === "number" && typeof b === "number") cmp = a - b; else cmp = String(a ?? "").localeCompare(String(b ?? ""), "it", { numeric: true, sensitivity: "base" }); return sort.dir === "asc" ? cmp : -cmp; });
    }
    return rows;
  }, [data, textFilters, numFilters, sort, columns, query, searchKeys]);

  const cycleSort = (key) => setSort((p) => (!p || p.key !== key ? { key, dir: "asc" } : p.dir === "asc" ? { key, dir: "desc" } : null));
  const isActive = (col) => (col.type === "number" ? !!numFilters[col.key] : !!textFilters[col.key]);

  if (data.length === 0) {
    return (
      <div>
        <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid={testid ? `${testid}-empty` : undefined}>
          {EmptyIcon && <EmptyIcon className="h-10 w-10 text-slate-300" />}
          <p className="font-heading text-lg font-semibold text-slate-700">{emptyText}</p>
        </div>
        {footer && footer([])}
      </div>
    );
  }

  return (
    <div>
    {searchKeys && (
      <div className="relative mb-3 max-w-xs">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={searchPlaceholder} data-testid={testid ? `${testid}-search` : "table-search"} className="pl-9" />
      </div>
    )}
    <div className="relative overflow-auto rounded-md border border-slate-100" style={{ maxHeight: "65vh" }}>
      <Table data-testid={testid}>
        <TableHeader className="sticky top-0 z-10 bg-white shadow-[inset_0_-1px_0_0_#e2e8f0]">
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col.key} className={col.align === "right" ? "text-right" : ""}>
                <span className={`inline-flex items-center gap-1 ${col.align === "right" ? "justify-end" : ""}`}>
                  <button onClick={() => cycleSort(col.key)} data-testid={testid ? `${testid}-sort-${col.key}` : undefined} className="inline-flex cursor-pointer select-none items-center gap-1 hover:text-slate-900">
                    {col.header}
                    {sort?.key === col.key ? (sort.dir === "asc" ? <ArrowUp className="h-3.5 w-3.5 text-[#002FA7]" /> : <ArrowDown className="h-3.5 w-3.5 text-[#002FA7]" />) : <ArrowUpDown className="h-3.5 w-3.5 text-slate-300" />}
                  </button>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid={testid ? `${testid}-filter-${col.key}` : undefined} className={`rounded p-0.5 hover:bg-slate-100 ${isActive(col) ? "text-[#002FA7]" : "text-slate-300"}`}><Filter className="h-3.5 w-3.5" fill={isActive(col) ? "currentColor" : "none"} /></button>
                    </PopoverTrigger>
                    <PopoverContent align={col.align === "right" ? "end" : "start"} className="w-auto p-3">
                      <div className="mb-2 flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => setSort({ key: col.key, dir: "asc" })} className="h-7 flex-1 text-xs"><ArrowUp className="mr-1 h-3 w-3" />Crescente</Button>
                        <Button variant="outline" size="sm" onClick={() => setSort({ key: col.key, dir: "desc" })} className="h-7 flex-1 text-xs"><ArrowDown className="mr-1 h-3 w-3" />Decrescente</Button>
                      </div>
                      {col.type === "number"
                        ? <NumberFilter col={col} filter={numFilters[col.key]} setFilter={setNumFil} />
                        : <TextFilter col={col} values={distinct[col.key] || []} selected={textFilters[col.key]} setSelected={setTextSel} />}
                    </PopoverContent>
                  </Popover>
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 ? (
            <TableRow><TableCell colSpan={columns.length} className="py-10 text-center text-slate-400">Nessun risultato con i filtri applicati.</TableCell></TableRow>
          ) : filtered.map((row, ri) => (
            <TableRow key={ri} data-testid={rowTestId ? rowTestId(row) : undefined} onClick={onRowClick ? () => onRowClick(row) : undefined} className={`hover:bg-slate-50 ${onRowClick ? "cursor-pointer" : ""}`}>
              {columns.map((col) => (
                <TableCell key={col.key} className={`${col.align === "right" ? "text-right" : ""} ${col.className || ""}`}>{col.cell ? col.cell(row) : row[col.key]}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    {footer && footer(filtered)}
    </div>
  );
}
